import {useMutation} from '@tanstack/react-query';
import React, {ReactElement, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {MAX_NUMBER_OF_ANSWERS} from '../../../../constants';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../incase-domain/components/button/Button';
import Label from '../../../../incase-domain/components/label/Label';
import {IElementQuestion} from '../../../../incase-domain/elements/question/question';
import {generateAnswers} from '../../../../service/languageModel.service';
import {useSaveAppliedRulesMutation} from '../../../../hooks/useSaveAppliedRules';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {isRuleInFocus} from '../../../../utils/rule';
import {removeArrayElement, swapArrayElement} from '../../../../utils/utils';
import SectionTitle from '../../../elements/common/SectionTitle';
import {TABS} from '../../form/part/tabs';
import ElementShadow from '../common/elementShadow';
import {COLOR_FIELDS} from '../common/fontAndBackgroundColorPicker';
import ChangeAnswer from './answers/actions/ChangeAnswer';
import AnswerActions, {AnswerActionType} from './answers/AnswerActions';
import Answers from './answers/Answers';
import AdvancedOptions from './components/advancedOptions/AdvancedOptions';
import QuestionOptions, {QUESTION_OPTIONS} from './components/QuestionOptions';
import QuestionText from './components/QustionText';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';
import type {TypedTranslation} from '../../../../i18n';

interface HighlightedQuestionElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedQuestionElementComponent(props: HighlightedQuestionElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const {mutate: saveAppliedRules} = useSaveAppliedRulesMutation();
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementQuestion;
    const {mutate, isLoading, isError, error} = useMutation(generateAnswers);
    const presentation = useSelector(selectedPresentationSelector);

    function addAnswer(): void {
        if (highlightedElement.answers.length < MAX_NUMBER_OF_ANSWERS) {
            const newAnswers = [
                ...highlightedElement.answers,
                {
                    text: `${t('presEditPage.editSidebar.elements.initialStates.answer')} ${highlightedElement.answers.length + 1}`,
                    type: highlightedElement.isDebatable ? 'debatable' : 'incorrect',
                    comment: '',
                },
            ];
            updateHighlightedElement.single('answers', newAnswers);
        } else {
            toast.info(`${t('toasts.maxAnswers')}`);
        }
    }

    const [numberOfAnswers, setNumberOfAnswers] = useState(4);

    async function handleGenerateAnswers(): Promise<void> {
        mutate(
            {question: highlightedElement.question, numberOfAnswers},
            {
                onSuccess: (data) => {
                    if (data.length > 0) {
                        updateHighlightedElement.single('answers', data);
                    }
                },
            }
        );
    }

    function removeAnswer(index: number): void {
        const newAnswers = removeArrayElement(highlightedElement.answers, index);
        const updatedRules = presentation.appliedRules
            ? presentation.appliedRules.filter((r) => !isRuleInFocus(r, highlightedElement.id, index))
            : [];

        updateHighlightedElement.single('answers', newAnswers);
        saveAppliedRules({appliedRules: updatedRules, presentationId: presentation._id});
    }

    function answerChangeHandler(index: number, value: string): void {
        const changedAnswer = {...highlightedElement.answers[index]};
        changedAnswer.text = value;

        const newAnswers = swapArrayElement(highlightedElement.answers, index, changedAnswer);
        updateHighlightedElement.single('answers', newAnswers);
    }

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <AdvancedOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    answerColorFields={[COLOR_FIELDS.CORRECT_COLOR, COLOR_FIELDS.INCORRECT_COLOR, COLOR_FIELDS.DEBATABLE_COLOR]}
                    commentColorFields={[
                        COLOR_FIELDS.CORRECT_COMMENT_COLOR,
                        COLOR_FIELDS.INCORRECT_COMMENT_COLOR,
                        COLOR_FIELDS.DEBATABLE_COMMENT_COLOR,
                    ]}
                />
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <QuestionOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    questionOptions={[
                        QUESTION_OPTIONS.SHOW_INDEX,
                        QUESTION_OPTIONS.SINGLE_CORRECT,
                        QUESTION_OPTIONS.BLOCKING_QUESTION,
                        QUESTION_OPTIONS.SHOW_IN_REPORT,
                        QUESTION_OPTIONS.SHOW_RESULTS,
                        QUESTION_OPTIONS.SHOW_ALL_COMMENTS,
                        QUESTION_OPTIONS.RETRY_QUESTION,
                        QUESTION_OPTIONS.SHOW_CORRECT_ANSWERS_COUNT,
                    ]}
                    maxNumberOfRetryAttempts={highlightedElement.singleCorrect ? highlightedElement.answers.length : 10}
                />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <QuestionText
                highlightedElement={highlightedElement}
                errors={errors}
                highlightedElementChangeHandler={updateHighlightedElement}
                showDebatable
            />
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.gpt.smartAutofill')} infoText={t('designSidepanel.gpt.generate')} />
                <div className="u-flex u-h-between u-v-center">
                    <div className="u-flex" style={{alignItems: 'start'}}>
                        <Label>{t('designSidepanel.gpt.generate')}</Label>
                        <input
                            type="number"
                            step={1}
                            min={2}
                            max={6}
                            onChange={(e) => setNumberOfAnswers(Number(e.target.value))}
                            value={numberOfAnswers}
                            style={{width: '40px', margin: '0 5px'}}
                        />
                        <Label>{t('designSidepanel.gpt.answers')}</Label>
                    </div>
                    <Button
                        leading="solar:rocket-2-linear"
                        variant="soft"
                        loading={isLoading}
                        color="secondary"
                        size="sm"
                        onClick={handleGenerateAnswers}
                    />
                </div>
                {isError && <div style={{fontSize: '0.8em'}}>{t((error as {message: TypedTranslation}).message)}</div>}
            </div>

            <Answers errors={errors} addAnswer={addAnswer} noOfAnswers={highlightedElement.answers.length}>
                {highlightedElement.answers.map((answer, index) => (
                    <React.Fragment key={index}>
                        <ChangeAnswer
                            answer={answer.text}
                            validationErrors={errors}
                            answerChangeHandler={answerChangeHandler}
                            index={index}
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                        />
                        <AnswerActions
                            index={index}
                            validationErrors={errors}
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                            actions={[AnswerActionType.CHANGE_COMMENT, AnswerActionType.REMOVE_ANSWER, AnswerActionType.ANSWER_RULES]}
                            removeAnswer={removeAnswer}
                        />
                    </React.Fragment>
                ))}
            </Answers>
        </>
    );
}

export default HighlightedQuestionElementComponent;
